<script setup>
import Index from "~/pages/index.vue";
import Heading from "~/components/atoms/Heading.vue";
import BoxIcon from "~/components/icons/BoxIcon.vue";
import CoinsIcon from "~/components/icons/CoinsIcon.vue";
import StarIcon from "~/components/icons/StarIcon.vue";
import StarOutlineIcon from "~/components/icons/StarOutlineIcon.vue";
import TrustpilotStarIcon from "~/components/icons/TrustpilotStarIcon.vue";
import TruckIcon from "~/components/icons/TruckIcon.vue";
import {useStartupStore} from "~/store/sturtupStore";

const props = defineProps({
  data: {
    type: Object,
  },
});

const startupStore = useStartupStore();
const {locale} = useI18n();

const bottomDescription = computed(() => {
  const description = props.data?.bottom_description;
  const html = description?.html;

  if (typeof html !== 'string' || !html.trim().startsWith('{')) {
    return description;
  }

  try {
    const translations = JSON.parse(html);
    const currentLocale = locale.value;
    const language = currentLocale.split(/[-_]/)[0];

    return {
      ...description,
      html: translations[currentLocale]
        ?? translations[language]
        ?? translations.sv
        ?? ''
    };
  } catch {
    return description;
  }
});
</script>


<template>
  <div class="container">
    <section
      class="city-hero"
      :class="{'city-hero--haiku': props.data.city !== 'Malmö'}"
    >
      <div class="city-copy">
        <Heading>{{ $t('city.title', {city: props.data.city}) }}</Heading>

        <div v-if="props.data.city === 'Malmö'" class="city-description">
          <Text>{{ $t('city.malmo.intro') }}</Text>
          <Heading tag="h2">{{ $t('city.malmo.showroom_heading') }}</Heading>
          <Text>
            {{ $t('city.malmo.showroom_before') }}
            <a :href="$t('meta.domain') + $t('menu.items.contact-us.link')">{{ $t('city.links.customer_service') }}</a>
            {{ $t('city.malmo.showroom_after') }}
          </Text>
          <Heading tag="h2">{{ $t('city.samples_heading') }}</Heading>
          <Text>
            {{ $t('city.malmo.samples_before') }}
            <a :href="$t('meta.domain') + $t('menu.items.sample.link')">{{ $t('city.links.samples') }}</a>
            {{ $t('city.malmo.samples_after') }}
          </Text>
          <Text>{{ $t('city.malmo.conclusion') }}</Text>
        </div>
        <div v-else class="city-description">
          <Text>{{ $t('city.rest.intro', {city: props.data.city}) }}</Text>
          <Heading tag="h2">{{ $t('city.rest.support_heading') }}</Heading>
          <Text>
            {{ $t('city.rest.support_before') }}
            <a :href="$t('meta.domain') + $t('menu.items.contact-us.link')">{{ $t('city.links.customer_service') }}</a>
            {{ $t('city.rest.support_after') }}
            <a :href="$t('meta.domain') + $t('menu.items.b2b.link')">{{ $t('city.links.b2b') }}</a>{{ $t('city.rest.b2b_after') }}
          </Text>
          <Heading tag="h2">{{ $t('city.samples_heading') }}</Heading>
          <Text>
            {{ $t('city.rest.samples_before') }}
            <a :href="$t('meta.domain') + $t('menu.items.sample.link')">{{ $t('city.links.samples') }}</a>
            {{ $t('city.rest.samples_after', {city: props.data.city}) }}
          </Text>
          <Text>{{ $t('city.rest.conclusion', {city: props.data.city}) }}</Text>
        </div>
      </div>
      <div
        class="city-image"
        :class="{'city-image--malmo': props.data.city === 'Malmö'}"
      >
        <picture>
          <source
            v-if="props.data.city !== 'Malmö'"
            media="(max-width: 991px)"
            srcset="/images/city/city-default-mobile.jpg"
          >
          <img
            :class="{
              'city-image__haiku': props.data.city !== 'Malmö',
              'city-image__malmo': props.data.city === 'Malmö'
            }"
            :src="props.data.city === 'Malmö' ? '/images/city/malmo-showroom.png' : '/images/city/city-default-desktop.jpg'"
            :alt="$t('city.image_alt', {city: props.data.city})"
          >
        </picture>
      </div>
    </section>

    <section class="city-benefits">
      <div class="city-benefit rating">
        <span class="rating-value">4.8</span>
        <StarIcon class="google-star"/>
        <span>{{ $t('rating.google_reviews') }}</span>
      </div>
      <div class="city-benefit rating">
        <span class="rating-value">{{ startupStore.trustpilot_rating || '4.7' }}</span>
        <TrustpilotStarIcon class="trustpilot-star"/>
        <span>{{ $t('rating.trustpilot') }}</span>
      </div>
      <a
        class="city-benefit"
        :href="$t('meta.domain') + $t('menu.items.warehouse.link')"
      >
        <span class="benefit-icon"><TruckIcon stroke-width="1.1"/></span>
        <span>{{ $t('city.benefits.delivery') }}</span>
      </a>
      <a
        class="city-benefit"
        :href="$t('meta.domain') + $t('menu.items.sample.link')"
      >
        <span class="benefit-icon"><BoxIcon stroke-width="1.26"/></span>
        <span>{{ $t('city.benefits.samples') }}</span>
      </a>
      <div class="city-benefit">
        <span class="benefit-icon"><StarOutlineIcon/></span>
        <span>{{ $t('city.benefits.top_rated') }}</span>
      </div>
      <a
        class="city-benefit"
        :href="$t('meta.domain') + $t('menu.items.contact-us.link')"
      >
        <span class="benefit-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="8" r="4"/>
            <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"/>
          </svg>
        </span>
        <span>{{ $t('city.benefits.support') }}</span>
      </a>
      <div class="city-benefit">
        <span class="benefit-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2"/>
            <path d="M16 3v4M8 3v4M3 10h18"/>
          </svg>
        </span>
        <span>{{ $t('city.benefits.pay_later') }}</span>
      </div>
      <div class="city-benefit">
        <span class="benefit-icon"><CoinsIcon class="coins-icon"/></span>
        <span>{{ $t('city.benefits.range') }}</span>
      </div>
    </section>
  </div>

  <Index :city="props.data.city" :breadcrumbs="props.data.breadcrumbs"
         :bottomDescription="bottomDescription"/>
</template>

<style scoped lang="scss">
.city-hero {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.city-hero--haiku {
  aspect-ratio: 2936 / 806;
  grid-template-rows: minmax(0, 1fr);

  .city-copy {
    min-height: 0;
  }

  .city-image {
    height: 100%;
    min-height: 0;
  }
}

.city-copy {
  align-items: flex-start;
  background: #f8f8f8;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 500px;
  padding: 48px 64px 48px 64px;

  h1 {
    color: $dark-black;
    font-size: 32px;
    font-style: normal;
    font-weight: 600;
    line-height: 1.18;
    margin-bottom: 24px;
  }

  h2 {
    color: $dark-black;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 1.35;
    margin-top: 24px;
    margin-bottom: 16px;
  }

  @include smd {
    min-height: auto;
    padding: 32px 24px;

    h1 {
      font-size: 28px;
    }

    h2 {
      font-size: 18px;
    }
  }
}

.city-description {
  gap: 0px;

  :deep(a) {
    text-decoration-line: underline;
    text-decoration-style: solid;
  }

  :deep(p) {
    color: $dark-grey;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.55;
    margin: 0;
  }
}

.city-image {
  min-height: 500px;

  picture {
    display: block;
    height: 100%;
    width: 100%;
  }

  img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  .city-image__haiku {
    object-position: center;
  }

  .city-image__malmo {
    object-position: 75% center;
  }
}

.city-benefits {
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 4px;
  margin-bottom: 4px
}

.city-benefit {
  align-items: center;
  background: #fafafa;
  border: 1px solid #fff;
  color: var(--Space-Black, #18181f);
  display: flex;
  flex-direction: row;
  font-family: "Avenir Next", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  gap: 11px;
  justify-content: center;
  line-height: normal;
  min-height: 128px;
  padding: 20px;
  text-align: center;
  text-decoration: none;

  &[href] > span:last-child {
    text-decoration: underline;
  }
}

.benefit-icon {
  align-items: center;
  background: #fff;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
  width: 36px;

  :deep(svg) {
    fill: none;
    height: 21px;
    stroke: #18181f;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: var(--icon-stroke-width, 1.5);
    width: 21px;
  }
}

.google-star {
  display: block;
  fill: #fcbf00;
  height: 22px;
  transform: translateY(-3px);
  width: 22px;
}

.benefit-icon :deep(svg.coins-icon) {
  fill: #18181f;
  stroke: none;
}

.trustpilot-star {
  height: 24px;
  width: 24px;
}

.rating-value {
  font-size: 16px;
}

@include smd {
  .container {
    padding-left: 0;
    padding-right: 0;
  }

  .city-hero {
    grid-template-columns: 1fr;
  }

  .city-hero--haiku {
    aspect-ratio: auto;
    grid-template-rows: auto;
  }

  .city-image {
    min-height: 300px;
  }

  .city-hero--haiku .city-image {
    aspect-ratio: 720 / 400;
    height: auto;
    min-height: 0;
  }

  .city-image--malmo {
    aspect-ratio: 1464 / 806;
    min-height: 0;

    .city-image__malmo {
      object-fit: contain;
      object-position: center;
    }
  }

  .city-benefits {
    gap: 2px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 2px;
    margin-bottom: 2px;
  }

  .city-benefit {
    font-size: clamp(10px, 2.9vw, 12px);
    font-weight: 500;
    gap: 6px;
    justify-content: left;
    line-height: 1.2;
    min-height: 80px;
    padding: 10px 12px;
    text-align: center;
    white-space: nowrap;
  }

  .benefit-icon {
    flex-basis: 32px;
    height: 32px;
    width: 32px;

    :deep(svg) {
      height: 19px;
      width: 19px;
    }
  }

  .rating-value {
    font-size: 12px;
  }

  .google-star,
  .trustpilot-star {
    height: 17px;
    width: 17px;
  }
}
</style>
